import axios from 'axios';
import {API_CONFIG} from '../../config/api.config';

const CMS_API_URL = API_CONFIG.CMS_URL;

/**
 * Get auth token from localStorage
 */
const getAuthToken = () => {
  return (
    localStorage.getItem('token') ||
    localStorage.getItem('cms_auth_token') ||
    ''
  );
};

/**
 * Get WhatsApp API key from localStorage
 */
const getWhatsAppApiKey = () => {
  return localStorage.getItem('whatsapp_api_key') || '';
};

/**
 * Create axios instance for CMS API
 */
const createCmsAxiosInstance = () => {
  const instance = axios.create({
    baseURL: CMS_API_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor to add auth token
  instance.interceptors.request.use(
    (config) => {
      const token = getAuthToken();
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  // Response interceptor to handle errors
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      // Handle 401 errors globally, but ignore WhatsApp proxy 401s (which mean bad WA api key, not CMS user logout)
      if (
        error.response?.status === 401 &&
        !error.config?.url?.includes('/api/whatsapp')
      ) {
        // Clear token on unauthorized
        localStorage.removeItem('cms_auth_token');
        localStorage.removeItem('cms_user');

        // Let React Router handle the redirect securely
        window.dispatchEvent(new CustomEvent('unauthorized'));
      }
      return Promise.reject(error);
    },
  );

  return instance;
};

const cmsClient = createCmsAxiosInstance();

/**
 * CMS API Service
 * Handles CMS backend API calls
 */
class CmsApiService {
  // ==================== Authentication ====================

  async login(email, password) {
    const response = await cmsClient.post('/api/auth/login', {email, password});
    const data = response.data;

    if (data.token) {
      localStorage.setItem('cms_auth_token', data.token);
      localStorage.setItem('cms_user', JSON.stringify(data.user));
    }

    return data;
  }

  async logout() {
    try {
      await cmsClient.post('/api/auth/logout');
    } finally {
      localStorage.removeItem('cms_auth_token');
      localStorage.removeItem('cms_user');
      localStorage.removeItem('whatsapp_api_key');
    }
  }

  async getMe() {
    const response = await cmsClient.get('/api/auth/me');
    return response.data;
  }

  async changePassword(currentPassword, newPassword) {
    const response = await cmsClient.post('/api/auth/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  }

  async updateProfile(profileData) {
    const response = await cmsClient.patch('/api/auth/me', profileData);
    return response.data;
  }

  // ==================== Users ====================

  async getUsers(params = {}) {
    const response = await cmsClient.get('/api/users', {params});
    return response.data;
  }

  async getUser(id) {
    const response = await cmsClient.get(`/api/users/${id}`);
    return response.data;
  }

  async createUser(userData) {
    const response = await cmsClient.post('/api/auth/register', userData);
    return response.data;
  }

  async updateUser(id, userData) {
    const response = await cmsClient.put(`/api/users/${id}`, userData);
    return response.data;
  }

  async deleteUser(id) {
    const response = await cmsClient.delete(`/api/users/${id}`);
    return response.data;
  }

  // ==================== Settings ====================

  async getSettings() {
    const response = await cmsClient.get('/api/settings');
    return response.data;
  }

  async getPublicSettings() {
    const response = await axios.get(`${CMS_API_URL}/api/settings/public`);
    return response.data;
  }

  async getSetting(key) {
    const response = await cmsClient.get(`/api/settings/${key}`);
    return response.data;
  }

  async updateSetting(key, value) {
    const response = await cmsClient.put(`/api/settings/${key}`, value);
    return response.data;
  }

  // ==================== Dashboard ====================

  async getDashboardStats() {
    const response = await cmsClient.get('/api/dashboard/stats');
    return response.data;
  }

  async getActivityLogs(params = {}) {
    const response = await cmsClient.get('/api/dashboard/activity', {params});
    return response.data;
  }

  // ==================== WhatsApp Proxy ====================

  async whatsAppLogin(email, password) {
    const response = await cmsClient.post('/api/whatsapp/admin/login', {
      email,
      password,
    });

    if (response.data.api_key) {
      localStorage.setItem('whatsapp_api_key', response.data.api_key);
    }

    return response.data;
  }

  async getWhatsAppAccounts() {
    const apiKey = getWhatsAppApiKey();
    if (!apiKey) return {accounts: []};

    const response = await cmsClient.get('/api/whatsapp/accounts', {
      headers: {'x-whatsapp-api-key': apiKey},
    });
    return response.data;
  }

  async getWhatsAppStatus() {
    const apiKey = getWhatsAppApiKey();
    if (!apiKey) return {accounts: []};

    const response = await cmsClient.get('/api/whatsapp/accounts/status', {
      headers: {'x-whatsapp-api-key': apiKey},
    });
    return response.data;
  }

  async getWhatsAppStats() {
    const apiKey = getWhatsAppApiKey();
    if (!apiKey) return null;

    const response = await cmsClient.get(
      '/api/whatsapp/messages/reports/stats',
      {
        headers: {'x-whatsapp-api-key': apiKey},
      },
    );
    return response.data;
  }

  async getWhatsAppConnect() {
    const apiKey = getWhatsAppApiKey();
    if (!apiKey) return {qr: null, status: 'unconfigured'};

    const response = await cmsClient.get('/api/whatsapp/accounts/connect', {
      headers: {'x-whatsapp-api-key': apiKey},
    });
    return response.data;
  }

  async sendMessage(messageData) {
    const response = await cmsClient.post(
      '/api/whatsapp/messages/send',
      messageData,
      {
        headers: {'x-whatsapp-api-key': getWhatsAppApiKey()},
      },
    );
    return response.data;
  }

  async sendBulkMessages(bulkData) {
    const response = await cmsClient.post(
      '/api/whatsapp/messages/bulk-send',
      bulkData,
      {
        headers: {'x-whatsapp-api-key': getWhatsAppApiKey()},
      },
    );
    return response.data;
  }

  async getMessageHistory(params = {}) {
    const apiKey = getWhatsAppApiKey();
    if (!apiKey) return {data: [], pagination: {}};

    const response = await cmsClient.get('/api/whatsapp/messages/history', {
      headers: {'x-whatsapp-api-key': apiKey},
      params,
    });
    return response.data;
  }

  async getMessageStats(params = {}) {
    const apiKey = getWhatsAppApiKey();
    if (!apiKey) return null;

    const response = await cmsClient.get(
      '/api/whatsapp/messages/reports/stats',
      {
        headers: {'x-whatsapp-api-key': apiKey},
        params,
      },
    );
    return response.data;
  }

  async getDailyStats(params = {}) {
    const response = await cmsClient.get(
      '/api/whatsapp/messages/reports/daily',
      {
        headers: {'x-whatsapp-api-key': getWhatsAppApiKey()},
        params,
      },
    );
    return response.data;
  }

  // ==================== Helpers ====================

  isAuthenticated() {
    return !!getAuthToken();
  }

  getStoredUser() {
    const user = localStorage.getItem('cms_user');
    return user ? JSON.parse(user) : null;
  }

  hasWhatsAppKey() {
    return !!getWhatsAppApiKey();
  }
}

const cmsApi = new CmsApiService();
export default cmsApi;
